import { useEffect, useState, ReactNode } from 'react';
import { Package, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useOrderDetailQuery, useOrderListQuery } from '@/client/api/payment/payment.query';
import {
  useCancelOrderMutation,
  useSetOrderStatusMutation,
} from '@/client/api/payment/payment.mutation';
import { formatMoney, Order, OrderListParams } from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { PaymentsFilterBar, SortableTh } from '@/client/components/admin/Payment/PaymentsFilterBar';
import {
  RelationshipLink,
  StoreRelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

const ORDER_STATUSES = [
  'pending',
  'paid',
  'processing',
  'shipped',
  'delivered',
  'canceled',
  'refunded',
];

const STATUS_OPTIONS = ORDER_STATUSES.map((s) => ({ value: s, label: s }));

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  canceled: 'bg-gray-100 text-gray-600',
  refunded: 'bg-red-100 text-red-800',
};

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing orders requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const OrderDetailsDrawer = ({
  orderid,
  isOpen,
  setIsOpen,
  canEdit,
  canVoid,
  onClose,
}: {
  orderid: number | null;
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  canEdit: boolean;
  canVoid: boolean;
  onClose: () => void;
}) => {
  const { data: order, isLoading } = useOrderDetailQuery(isOpen ? orderid : null);
  const setStatus = useSetOrderStatusMutation();
  const cancelOrder = useCancelOrderMutation();

  const [status, setStatusValue] = useState('');
  const [tracking, setTracking] = useState('');

  useEffect(() => {
    if (!order) return;
    setStatusValue(String(order.status || ''));
    setTracking(order.tracking_number || '');
  }, [order]);

  const busy = setStatus.isPending || cancelOrder.isPending;

  if (!isOpen) return null;

  const currentStatus = String(order?.status || '');

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            {isLoading ? 'Loading…' : `Order #${order?.orderid ?? orderid}`}
          </DrawerTitle>
          <DrawerDescription>{currentStatus || 'Order details'}</DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {!isLoading && order && (
            <>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                  STATUS_BADGE[currentStatus] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {currentStatus.toUpperCase() || 'UNKNOWN'}
              </span>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-2xl font-black text-gray-900">
                  {formatMoney(order.total, order.ccode)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="Order ID" value={order.orderid} />
                <InfoRow label="Buyer" value={<UserRelationshipLink uid={order.uid} summary={order.user_summary} />} />
                <InfoRow
                  label="Store"
                  value={<StoreRelationshipLink storeid={order.storeid} summary={order.store_summary} />}
                />
                <InfoRow
                  label="Invoice"
                  value={order.invid != null ? <RelationshipLink kind="invoice" id={order.invid} /> : '—'}
                />
                <InfoRow
                  label="Payment"
                  value={order.pid != null ? <RelationshipLink kind="payment" id={order.pid} /> : '—'}
                />
                <InfoRow label="Tracking" value={order.tracking_number || '—'} />
                <InfoRow label="Added" value={formatDate(order.date_added)} />
                <InfoRow label="Paid" value={formatDate(order.date_paid)} />
              </div>

              {canEdit && (
                <div className="space-y-3 rounded-lg border border-gray-100 p-3">
                  <div className="space-y-1.5">
                    <Label>Set status</Label>
                    <select
                      value={status || currentStatus}
                      onChange={(e) => setStatusValue(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      {ORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tracking number (optional)</Label>
                    <Input
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder="Tracking #"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || !(status || currentStatus)}
                    onClick={async () => {
                      try {
                        await setStatus.mutateAsync({
                          orderid: order.orderid,
                          status: status || currentStatus,
                          ...(tracking.trim() ? { tracking_number: tracking.trim() } : {}),
                        });
                        toast.success('Order status updated', { richColors: true });
                      } catch (error: any) {
                        toast.error(error?.message || 'Failed to update status', {
                          richColors: true,
                        });
                      }
                    }}
                  >
                    {setStatus.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                    Update status
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canVoid && order && currentStatus !== 'canceled' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={async () => {
                  try {
                    await cancelOrder.mutateAsync(order.orderid);
                    toast.success('Order canceled', { richColors: true });
                  } catch (error: any) {
                    toast.error(error?.message || 'Failed to cancel order', { richColors: true });
                  }
                }}
              >
                {cancelOrder.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Cancel order
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const OrderListPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Orders' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const editPerm = useHasPermission('payment.edit');
  const voidPerm = useHasPermission('payment.void');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;
  const canVoid = voidPerm.isSuperAdmin || voidPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { state, offset, patch, clearFilters, setDetail, toggleSort, toApiNumber } =
    usePaymentListUrlState();

  const listParams: OrderListParams = {
    limit: state.limit,
    offset,
    ...(state.q.trim() ? { q: state.q.trim() } : {}),
    ...(state.status ? { status: state.status } : {}),
    ...(state.uid ? { uid: toApiNumber(state.uid) } : {}),
    ...(state.storeid ? { storeid: toApiNumber(state.storeid) } : {}),
    ...(state.amount_min ? { amount_min: toApiNumber(state.amount_min) } : {}),
    ...(state.amount_max ? { amount_max: toApiNumber(state.amount_max) } : {}),
    ...(state.sort_by ? { sort_by: state.sort_by, order: state.order || 'desc' } : {}),
  };

  const { data, isLoading, isError, error } = useOrderListQuery(
    listParams,
    canView && !permLoading,
  );

  const orders = data?.orders ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <Package className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Orders</h1>
          </div>
          <p className="text-sm text-gray-500">Update status, tracking, or cancel orders.</p>
        </div>

        <PaymentsFilterBar
          state={state}
          onPatch={patch}
          onClear={() => clearFilters()}
          statusOptions={STATUS_OPTIONS}
          showAmount
          showDates={false}
          showOid={false}
          showCcode={false}
          searchPlaceholder="Search ffid, uuid, or id…"
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load orders'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Order ID
                  </th>
                  <SortableTh
                    label="Total"
                    field="amount"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Buyer
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Store
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Invoice
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Payment
                  </th>
                  <SortableTh
                    label="Date"
                    field="date_added"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-16" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && orders.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-sm text-gray-500">
                      No orders found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  orders.map((order: Order) => (
                    <tr
                      key={order.orderid}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setDetail('orderid', order.orderid)}
                    >
                      <td className="px-6 py-4 font-mono text-sm font-semibold">
                        {order.orderid}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold">
                        {formatMoney(order.total, order.ccode)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            STATUS_BADGE[String(order.status)] || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {String(order.status || '—').toUpperCase()}
                        </span>
                      </td>
                      <td className="max-w-[160px] px-6 py-4">
                        <UserRelationshipLink uid={order.uid} summary={order.user_summary} />
                      </td>
                      <td className="max-w-[160px] px-6 py-4">
                        <StoreRelationshipLink storeid={order.storeid} summary={order.store_summary} />
                      </td>
                      <td className="px-6 py-4">
                        {order.invid != null ? (
                          <RelationshipLink kind="invoice" id={order.invid} />
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {order.pid != null ? (
                          <RelationshipLink kind="payment" id={order.pid} />
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(order.date_added)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={(page) => patch({ page }, false)}
              setItemsPerPage={(limit) => patch({ limit, page: 1 }, false)}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <OrderDetailsDrawer
        orderid={state.orderid}
        isOpen={state.orderid != null}
        setIsOpen={(open) => {
          if (!open) setDetail('orderid', null);
        }}
        canEdit={canEdit}
        canVoid={canVoid}
        onClose={() => setDetail('orderid', null)}
      />
    </div>
  );
};

export default OrderListPage;
